import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, IndianRupee } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, fmtINR } from "../lib/api";
import { getPack } from "../lib/offline";

/**
 * Full floor-wise assessment editor for field forms (collector view).
 * NO rates are shown here — amounts are computed silently against the
 * Rate Engine configured by the Municipal Admin.
 */
export function useAssessmentMasters(code) {
  const [data, setData] = useState({ fyId: "", fys: [], usageTypes: [], subUsages: [], propertyTypes: [], constTypes: [], roadWidths: [], feeCats: [], feeSubsByCat: {}, docTypes: [] });
  useEffect(() => {
    Promise.all([
      api.get(`/t/${code}/masters/financial-years`).catch(() => ({ data: null })),
      api.get(`/t/${code}/masters/usage-types`).catch(() => ({ data: null })),
      api.get(`/t/${code}/masters/sub-usage-types`).catch(() => ({ data: null })),
      api.get(`/t/${code}/masters/property-types`).catch(() => ({ data: null })),
      api.get(`/t/${code}/rate-engine/user-fee-rates`).catch(() => ({ data: null })),
      api.get(`/t/${code}/masters/construction-types`).catch(() => ({ data: null })),
      api.get(`/t/${code}/masters/road-widths`).catch(() => ({ data: null })),
      api.get(`/t/${code}/masters/document-types`).catch(() => ({ data: null })),
    ]).then(async ([fys, ut, su, pt, fr, ct, rw, dt]) => {
      // offline fallback: fill any failed fetch from the cached offline pack
      if ([fys, ut, su, pt, fr, ct, rw, dt].some((r) => r.data === null)) {
        const pack = await getPack(code).catch(() => null);
        const m = pack?.masters || {};
        fys = { data: fys.data ?? m["financial-years"] ?? [] };
        ut = { data: ut.data ?? m["usage-types"] ?? [] };
        su = { data: su.data ?? m["sub-usage-types"] ?? [] };
        pt = { data: pt.data ?? m["property-types"] ?? [] };
        fr = { data: fr.data ?? pack?.fee_rates ?? [] };
        ct = { data: ct.data ?? m["construction-types"] ?? [] };
        rw = { data: rw.data ?? m["road-widths"] ?? [] };
        dt = { data: dt.data ?? m["document-types"] ?? [] };
      }
      const cur = (fys.data || []).find((f) => f.is_current) || (fys.data || [])[0];
      const feeCats = [];
      const seen = {};
      const feeSubsByCat = {};
      (fr.data || []).forEach((r) => {
        if (r.category_id && !seen[r.category_id]) {
          seen[r.category_id] = true;
          feeCats.push({ id: r.category_id, name: r.category_name });
        }
        if (r.category_id && r.sub_category_id) {
          feeSubsByCat[r.category_id] = feeSubsByCat[r.category_id] || [];
          if (!feeSubsByCat[r.category_id].some((s) => s.id === r.sub_category_id)) {
            feeSubsByCat[r.category_id].push({ id: r.sub_category_id, name: r.sub_category_name });
          }
        }
      });
      setData({ fyId: cur?.id || "", fys: fys.data || [], usageTypes: ut.data || [], subUsages: su.data || [], propertyTypes: pt.data || [], constTypes: ct.data || [], roadWidths: (rw.data || []).filter((w) => w.is_active !== false), feeCats, feeSubsByCat, docTypes: (dt.data || []).filter((d) => d.is_active !== false) });
    });
  }, [code]);
  return data;
}

const FloorSelect = ({ placeholder, options, value, onChange, disabled, testId }) => {
  const sel = options.find((o) => o.id === value);
  return (
    <Select value={value || ""} onValueChange={(v) => v && onChange(v)} disabled={disabled}>
      <SelectTrigger className="h-10" data-testid={testId}>
        <SelectValue placeholder={placeholder}>{sel?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const emptyFloor = (idx = 0) => {
  const labels = ["Ground Floor", "First Floor", "Second Floor", "Third Floor", "Fourth Floor", "Fifth Floor"];
  return { floor_label: labels[idx] || `Floor ${idx + 1}`, house_no: "", property_type_id: "", const_type_id: "", usage_type_id: "", sub_usage_id: "",
           fee_category_id: "", fee_sub_category_id: "", plot_area_sqft: "", open_area_sqft: "", area_sqft: "", carpet_area_sqft: "",
           electricity_ca_no: "", construction_date: "", construction_age: "" };
};

export const FloorEditorFull = ({ masters, floors, setFloors, readOnly = false, testIdPrefix = "fef" }) => {
  const update = (i, patch) => setFloors(floors.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const remove = (i) => setFloors(floors.filter((_, idx) => idx !== i));
  const add = () => setFloors([...floors, emptyFloor(floors.length)]);

  const opts = useMemo(() => ({
    ptype: (masters.propertyTypes || []).map((p) => ({ id: p.id, label: p.type_name })),
    ctype: (masters.constTypes || []).map((c) => ({ id: c.id, label: c.const_type_name })),
    usage: (masters.usageTypes || []).map((u) => ({ id: u.id, label: u.usage_type_name })),
    subUsage: (masters.subUsages || []).map((s) => ({ id: s.id, label: s.sub_usage_type_name || s.sub_usage_name })),
    feeCat: (masters.feeCats || []).map((c) => ({ id: c.id, label: c.name })),
  }), [masters]);

  const tot = (key) => floors.reduce((s, f) => s + (Number(f[key]) || 0), 0);

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-center justify-between">
        <Label>Floor-wise details</Label>
        {!readOnly && (
          <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testIdPrefix}-add-floor`}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add floor
          </Button>
        )}
      </div>
      {floors.length === 0 && (
        <p className="text-xs text-muted-foreground border border-dashed rounded-lg py-3 text-center">No floors recorded.</p>
      )}
      {floors.map((f, i) => (
        <div key={i} className="border rounded-lg p-2.5 space-y-2" data-testid={`${testIdPrefix}-floor-${i}`}>
          <div className="flex items-center gap-2">
            <Input className="h-10 flex-1" placeholder="Floor label" value={f.floor_label || ""} disabled={readOnly}
              onChange={(e) => update(i, { floor_label: e.target.value })} data-testid={`${testIdPrefix}-label-${i}`} />
            <Input className="h-10 w-32" placeholder="House No." value={f.house_no || ""} disabled={readOnly}
              onChange={(e) => update(i, { house_no: e.target.value })} data-testid={`${testIdPrefix}-houseno-${i}`} />
            {!readOnly && (
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground" onClick={() => remove(i)}
                data-testid={`${testIdPrefix}-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FloorSelect placeholder="Property type" options={opts.ptype} value={f.property_type_id} disabled={readOnly}
              onChange={(v) => update(i, { property_type_id: v })} testId={`${testIdPrefix}-ptype-${i}`} />
            <FloorSelect placeholder="Construction type" options={opts.ctype} value={f.const_type_id} disabled={readOnly}
              onChange={(v) => update(i, { const_type_id: v })} testId={`${testIdPrefix}-ctype-${i}`} />
            <FloorSelect placeholder="Usage type" options={opts.usage} value={f.usage_type_id} disabled={readOnly}
              onChange={(v) => update(i, { usage_type_id: v })} testId={`${testIdPrefix}-usage-${i}`} />
            <FloorSelect placeholder="Sub-usage" options={opts.subUsage} value={f.sub_usage_id} disabled={readOnly}
              onChange={(v) => update(i, { sub_usage_id: v })} testId={`${testIdPrefix}-subusage-${i}`} />
            <FloorSelect placeholder="Fee category (SWM)" options={opts.feeCat} value={f.fee_category_id} disabled={readOnly}
              onChange={(v) => update(i, { fee_category_id: v, fee_sub_category_id: "" })} testId={`${testIdPrefix}-feecat-${i}`} />
            {f.fee_category_id && (masters.feeSubsByCat[f.fee_category_id] || []).length > 0 && (
              <FloorSelect placeholder="Fee sub-category" value={f.fee_sub_category_id} disabled={readOnly}
                options={(masters.feeSubsByCat[f.fee_category_id] || []).map((s) => ({ id: s.id, label: s.name }))}
                onChange={(v) => update(i, { fee_sub_category_id: v })} testId={`${testIdPrefix}-feesub-${i}`} />
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[["plot_area_sqft", "Plot sqft"], ["open_area_sqft", "Open sqft"], ["area_sqft", "Constructed sqft"], ["carpet_area_sqft", "Carpet sqft"]].map(([k, ph]) => (
              <div key={k} className="space-y-0.5">
                <span className="text-[10px] text-muted-foreground">{ph}</span>
                <Input className="h-10" type="number" placeholder={ph} value={f[k] ?? ""} disabled={readOnly}
                  onChange={(e) => {
                    const val = e.target.value === "" ? "" : Number(e.target.value);
                    const patch = { [k]: val };
                    if (k === "plot_area_sqft" || k === "open_area_sqft") {
                      // constructed auto = plot - open (carpet follows at 80%)
                      const plot = Number(k === "plot_area_sqft" ? val : f.plot_area_sqft) || 0;
                      const open = Number(k === "open_area_sqft" ? val : f.open_area_sqft) || 0;
                      if (plot > 0) {
                        const constructed = Math.max(0, Math.round((plot - open) * 100) / 100);
                        patch.area_sqft = constructed;
                        patch.carpet_area_sqft = Math.round(constructed * 0.8 * 100) / 100;
                      }
                    }
                    if (k === "area_sqft" && val !== "") patch.carpet_area_sqft = Math.round(val * 0.8 * 100) / 100;
                    update(i, patch);
                  }} data-testid={`${testIdPrefix}-${k}-${i}`} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="space-y-0.5">
              <span className="text-[10px] text-muted-foreground">Electricity CA No</span>
              <Input className="h-10" placeholder="CA number" value={f.electricity_ca_no || ""} disabled={readOnly}
                onChange={(e) => update(i, { electricity_ca_no: e.target.value })} data-testid={`${testIdPrefix}-electricity-${i}`} />
            </div>
            <div className="space-y-0.5">
              <span className="text-[10px] text-muted-foreground">Construction date</span>
              <Input className="h-10" type="date" value={f.construction_date || ""} disabled={readOnly}
                onChange={(e) => {
                  const d = e.target.value;
                  const patch = { construction_date: d };
                  if (d) {
                    const age = new Date().getFullYear() - new Date(d).getFullYear();
                    if (age >= 0) patch.construction_age = age;  // auto-calculated, still editable below
                  }
                  update(i, patch);
                }} data-testid={`${testIdPrefix}-constdate-${i}`} />
            </div>
            <div className="space-y-0.5">
              <span className="text-[10px] text-muted-foreground">Construction age (yrs, auto)</span>
              <Input className="h-10" type="number" min="0" placeholder="Years" value={f.construction_age ?? ""} disabled={readOnly}
                onChange={(e) => update(i, { construction_age: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid={`${testIdPrefix}-constage-${i}`} />
            </div>
          </div>
        </div>
      ))}
      {floors.length > 0 && (
        <div className="grid grid-cols-4 gap-1 text-[11px] text-muted-foreground px-1" data-testid={`${testIdPrefix}-totals`}>
          <span>Plot: <b className="text-foreground">{tot("plot_area_sqft").toLocaleString("en-IN")}</b></span>
          <span>Open: <b className="text-foreground">{tot("open_area_sqft").toLocaleString("en-IN")}</b></span>
          <span>Constructed: <b className="text-foreground">{tot("area_sqft").toLocaleString("en-IN")}</b></span>
          <span>Carpet: <b className="text-foreground">{tot("carpet_area_sqft").toLocaleString("en-IN")}</b></span>
        </div>
      )}
    </div>
  );
};

/**
 * Silent demand computation: calls the admin Rate Engine in the background and
 * shows ONLY the new demand generation amount (no rates visible to the collector).
 */
export const useSilentDemandCalc = (code, fyId, floors, ownerName, extra = {}) => {
  const [calc, setCalc] = useState(null);
  const timer = useRef(null);
  // fyId may be a single id or an array of FY ids (multi-year demand capture)
  const fyIds = (Array.isArray(fyId) ? fyId : [fyId]).filter(Boolean);
  const floorsKey = JSON.stringify([(floors || []).map((f) => [f.usage_type_id, f.area_sqft, f.carpet_area_sqft, f.const_type_id, f.open_area_sqft, f.fee_category_id, f.fee_sub_category_id]), extra.road_width_id || null, fyIds]);

  useEffect(() => {
    if (fyIds.length === 0) return;
    const valid = (floors || []).filter((f) => Number(f.area_sqft) > 0 && f.usage_type_id);
    if (valid.length === 0) { setCalc(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const vacantArea = (floors || []).reduce((s, f) => s + (Number(f.open_area_sqft) || 0), 0);
        const mkBody = (fid) => ({
          financial_year_id: fid,
          road_width_id: extra.road_width_id || null,
          vacant_area_sqft: vacantArea || null,
          floors: valid.map((f) => ({
            floor_label: f.floor_label, owner_name: ownerName || null,
            usage_type_id: f.usage_type_id, area_sqft: Number(f.area_sqft),
            carpet_area_sqft: Number(f.carpet_area_sqft) || null,
            const_type_id: f.const_type_id || null,
            fee_category_id: f.fee_category_id || null, fee_sub_category_id: f.fee_sub_category_id || null,
          })),
        });
        const results = [];
        for (const fid of fyIds) {
          const r = await api.post(`/t/${code}/rate-engine/calculate`, mkBody(fid));
          results.push(r.data);
        }
        const first = results[0];
        const combined = fyIds.length === 1 ? first : {
          ...first,
          multi_year: true,
          per_year: results.map((r) => ({ financial_year_id: r.financial_year_id, fy_label: r.fy_label,
            arv_total_yearly: r.arv_total_yearly, total_yearly: r.grand_total?.total_yearly || 0 })),
          proposed_demands: results.flatMap((r) => r.proposed_demands || []),
          grand_total: { total_yearly: Math.round(results.reduce((s, r) => s + (r.grand_total?.total_yearly || 0), 0) * 100) / 100 },
          warnings: [...new Set(results.flatMap((r) => r.warnings || []))],
        };
        setCalc({ result: combined, inputs: { fyIds, floors: valid } });
      } catch { setCalc(null); }
    }, 700);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, floorsKey, ownerName]);
  return calc;
};

export const NewDemandAmount = ({ calc, title = "New demand generation amount" }) => (
  <Card className="shadow-sm border-primary/30" data-testid="new-demand-amount-card">
    <CardHeader className="pb-1">
      <CardTitle className="text-sm font-heading flex items-center gap-2"><IndianRupee className="h-4 w-4" /> {title}</CardTitle>
    </CardHeader>
    <CardContent className="p-3 pt-1">
      {!calc?.result ? (
        <p className="text-xs text-muted-foreground">Enter floor details (usage + constructed area) — the amount is computed automatically from the municipality's rate configuration.</p>
      ) : (
        <div className="space-y-1.5">
          {calc.result.multi_year ? (
            (calc.result.per_year || []).map((y, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">FY {y.fy_label || ""} — ARV {fmtINR(y.arv_total_yearly)}</span>
                <span className="tabular-nums font-medium">{fmtINR(y.total_yearly)}</span>
              </div>
            ))
          ) : calc.result.arv_total_yearly > 0 && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Annual Value (ARV)</span>
              <span className="tabular-nums font-medium" data-testid="new-demand-arv">{fmtINR(calc.result.arv_total_yearly)}</span>
            </div>
          )}
          {(calc.result.proposed_demands || []).map((d, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {calc.result.multi_year && d.fy_label ? `${d.fy_label} • ` : ""}
                {d.label || (d.tax_code === "UC" ? "User Charges" : "Property Tax")}
                {d.percent ? ` (${d.percent}%)` : ""}
              </span>
              <span className="tabular-nums font-medium">{fmtINR(d.current_amount)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between border-t pt-1.5 font-semibold text-sm">
            <span>Total (yearly)</span>
            <span className="tabular-nums" data-testid="new-demand-total">{fmtINR(calc.result.grand_total?.total_yearly || 0)}</span>
          </div>
          {(calc.result.warnings || []).map((w, i) => (
            <p key={i} className="text-[11px] text-amber-700">{w}</p>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);
